import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import queryString from 'query-string';

import { changeAdmin, fetchUser, fetchUsers, inviteAll, updateAttributes } from '~src/modules/users/actions/user.actions';
import { PaginationI } from '~src/interfaces/pagination.interface';
import UserList from '~src/modules/users/components/UserList';
import TextBtnPageFooter from '~src/shared/footers/textBtnPageFooter/TextBtnPageFooter';
import ListPageHeader from '~src/shared/headers/listPageHeader/ListPageHeader';
import ItemsAmount from '~src/shared/lists/itemsAmount/ItemsAmount';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { AttributesRespI } from '~src/modules/attributes/interfaces';
import AddUserForm from '~src/modules/users/components/AddUserForm';
import { StoreI } from '~src/rootReducer';
import { fetchAttributesList, FetchAttributesParams } from '~src/modules/attributes/actions/attributes.actions';
import { UserFormValues, UsersRespI } from '~src/modules/users/interfaces';
import SortSelector, { SortingOption } from '~src/shared/sortSelector/SortSelector';
import { userSortingOptions } from '~src/modules/users/constants/sortingOptions';
import logger from '~src/services/logger/LlsLogger';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import { SortingValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import Pagination from '~src/shared/lists/pagination/Pagination';
import { fetchLocales, fetchTimeZones } from '~src/store/session/actions';
import SimpleSearchInput from '~src/shared/form/inputs/SimpleSearchInput';
import { ListMargin, PageContentContainer, SpinnerMargin } from '~src/styles/containers.sc';
import { routes } from '~src/routingConfig';
import { AttributeI } from '~src/interfaces/attributes.types';
import { getAttributes } from '~src/modules/users/reducers/userManagementReducer';
import { FilterI } from '~src/shared/lists/listFilters/ListFilters';
import { translateContent } from '~src/services/translationServices';
import { showNotification } from '~src/store/app/actions';
import { NotificationI } from '~src/store/app/interfaces';
import { CompanyI } from '~src/interfaces/company.interface';
import DropdownBtn from '~src/shared/menus/dropdownBtn/DropdownBtn';
import { MenuItem } from '~src/shared/menus/dropdownBtn/DropdownBtn.sc';
import CheckBox from '~src/shared/form/Checkbox';
import { User } from '~src/interfaces/user.interface';

import {
  DeactivatedWrapper,
  FilterFilterCol,
  FilterSortCol,
  InfoSortingBlock,
  SearchWrapper,
} from './userManagement.sc';
import { ConfirmationModal } from '~src/shared/ConfirmationModal';
import {objectDiff} from "~src/utils/objectDiff";

export interface FilterAndSortingConf {
  sort?: SortingValues;
  page?: number;
  search?: string;
  'filter.group'?: string;
  'filter.deactivated'?: boolean;
  open?: string;
}

type attrFilter = FilterI & {
  options: ReadonlyArray<Readonly<SortingOption>>;
  activeSelection: string;
};

interface DispatchProps {
  fetchUser: (userId: string) => ApiReduxResp<User>;
  fetchUsers: (params: FilterAndSortingConf) => ApiReduxResp<UsersRespI>;
  fetchTimeZones: () => ApiReduxResp<{ [key: string]: string }>;
  fetchLocales: () => ApiReduxResp<{ [key: string]: string }>;
  fetchAttributesList: (params?: FetchAttributesParams) => ApiReduxResp<AttributesRespI>;
  updateAttributes: (userId: string, value: { [key: string]: any }) => ApiReduxResp<null>;
  changeAdmin: (userId: string, isAdmin: boolean) => ApiReduxResp<null>;
  inviteAll: (section?: string) => ApiReduxResp<null>;
  showNotification: (notification: NotificationI) => void;
}

interface StateProps {
  pagination: PaginationI | null;
  loadingUsers: boolean;
  attributes: AttributeI[];
  company: CompanyI | null;
}

type Props = DispatchProps & StateProps & WithTranslation & RouteComponentProps<any>;

enum InviteTo {
  spaceloop = 'spaceloop',
}

interface State {
  formToggle: boolean;
  sort?: SortingValues;
  initialLoading: boolean;
  page?: number;
  search?: string;
  showDeactivated: boolean;
  attributes: AttributeI[];
  selectedAttrFilters: Record<string, string>; // attrId: optionId
  openItem?: string;
  isShowingConfirmationModal: boolean;
  inviteTo?: InviteTo;
}

class UserManagement extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = this.urlToState();
  }

  componentDidMount() {
    this.setState(this.urlToState());
    Promise.all([
      this.props.fetchAttributesList(),
      this.props.fetchTimeZones(),
      this.props.fetchLocales(),
      this.state.openItem && this.props.fetchUser(this.state.openItem),
      this.fetchUsers(),
    ]).then(() => {
      this.setState({ initialLoading: false });
    });
  }

  fetchUsers = () => {
    return this.props
      .fetchUsers(this.stateToConfig(true))
      .then(({ error }) => {
        if (error) {
          throw new Error('Error fetching users list');
        }
        this.setUrlBasedOnState();
      })
      .catch(error => {
        logger.info('Error fetching users list', {
          error,
          info: {
            componentStack: 'UsersList.tsx',
          },
        });
      });
  };

  showConfirmationModal = () => {
    this.setState(prevState => ({
      ...prevState,
      isShowingConfirmationModal: !prevState.isShowingConfirmationModal,
    }));
  };

  inviteAll = (section?: InviteTo) => {
    this.props.inviteAll(section).then(rsp => {
      if (rsp.error) {
        return;
      }
      this.props.showNotification({
        id: 'invite_all_success',
        type: 'info',
        txtKey: 'x.users.invite_all_successfull',
        persistent: false,
      });
    });

    this.onCancelInvite();
  };

  inviteAllToSpaceloop = () => {
    this.setState(prevState => ({
      ...prevState,
      inviteTo: InviteTo.spaceloop,
      isShowingConfirmationModal: !prevState.isShowingConfirmationModal,
    }));
  };

  inviteAllToLoopNow = () => {
    this.setState(prevState => ({
      ...prevState,
      inviteTo: undefined,
      isShowingConfirmationModal: !prevState.isShowingConfirmationModal,
    }));
  };

  onCancelInvite = () => {
    this.setState(prevState => {
      return {
        ...prevState,
        inviteTo: undefined,
        isShowingConfirmationModal: false,
      };
    });
  };

  updateAttributes = (userId: string, values: UserFormValues, initialValues: Partial<UserFormValues>) => {
    const { t } = this.props;
    let permissionPromise: Promise<any> = Promise.resolve();
    if (values.permissions && values.permissions.admin !== initialValues.permissions!.admin) {
      permissionPromise = this.props.changeAdmin(userId, values.permissions.admin);
    }
    const changedAttributeValues = objectDiff(initialValues.attributes || {}, values.attributes);
    let attributePromise: Promise<any> = Promise.resolve();
    if (Object.keys(changedAttributeValues).length > 0) {
      attributePromise = this.props.updateAttributes(userId, changedAttributeValues);
    }
    return Promise.all([attributePromise, permissionPromise])
      .then(responses => {
        responses.forEach(response => {
          if (response && response.error) {
            const errors: { [key: string]: any } = { _error: t('x.users.save_error') };
            Object.keys(response.error.response.data).forEach(k => {
              [errors[k]] = response.error.response.data[k];
            });
            return errors;
          }
        });
      })
      .then(this.fetchUsers)
      .catch(error => {
        throw error;
      });
  };

  changeSorting = (sort: SortingValues) => this.setState({ ...this.state, sort }, this.fetchUsers);

  changePage = (page: number) => this.setState({ ...this.state, page }, this.fetchUsers);

  changeOpenItem = (userId?: string) => {
    this.setState({ ...this.state, openItem: userId }, () => {
      this.setUrlBasedOnState();
    });
  };

  onChangeInput = (search: string) => {
    return new Promise<void>(res => {
      this.setState({ ...this.state, search }, () => {
        this.fetchUsers().then(res);
      });
    });
  };

  onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 13) {
      // @ts-ignore somehow it takes the wrong interface for HTMLInputElement
      this.setState({ search: e.target.value }, this.fetchUsers);
    }
  };

  setShowDeactivated = (showDeactivated: boolean) => {
    this.setState(
      {
        ...this.state,
        showDeactivated,
      },
      this.fetchUsers,
    );
  };

  goToImport = () => {
    this.props.history.push(routes.company.settings, { section: 'import' });
  };

  applyFilters = (attribute: string): ((option: string) => void) => {
    return (option: string) => {
      const selectedAttrFilters = Object.assign({}, this.state.selectedAttrFilters);
      if (option) {
        selectedAttrFilters[attribute] = option;
      } else {
        delete selectedAttrFilters[attribute];
      }

      this.setState({ selectedAttrFilters }, this.fetchUsers);
    };
  };

  getFilters = (): attrFilter[] =>
    this.props.attributes
      .filter(filter => filter.internal_type === 'option' && filter.attribute_options.length > 0)
      .map(f => {
        let activeSelection = '';
        const options: ReadonlyArray<Readonly<SortingOption>> = f.attribute_options.map(ao => {
          const aoName = translateContent(ao.value);
          if (this.state.selectedAttrFilters[f.uuid] && this.state.selectedAttrFilters[f.uuid] === ao.uuid) {
            activeSelection = ao.uuid;
          }
          return { name: ao.uuid, translationKey: aoName };
        });
        return {
          id: f.uuid,
          labelKey: translateContent(f.name),
          options: [{ name: '', translationKey: 'x.surveys.list.status_filter.all' }, ...options],
          activeSelection,
        };
      });

  render() {
    const { t, history, location, pagination } = this.props;

    const menuItems: MenuItem[] = [
      {
        text: {
          key: 'x.users.import_users_button',
          txt: t('x.users.import_users_button'),
        },
        onClick: this.goToImport,
      },
    ];

    if (
      this.props.company &&
      (this.props.company.features.okr || this.props.company.features.survey)
    ) {
      menuItems.push({
        text: {
          key: 'x.users.invite_users_button',
          txt: t('x.users.invite_users_button'),
        },
        onClick: this.inviteAllToLoopNow,
      });
    }

    if (this.props.company && this.props.company.features.booking) {
      menuItems.push({
        text: {
          key: 'x.users.invite_users_to_spaceloop_button',
          txt: t('x.users.invite_users_to_spaceloop_button'),
        },
        onClick: this.inviteAllToSpaceloop,
      });
    }

    return (
      <main>
        <ListPageHeader t={t} titleTxt="x.users.headline" subTitleTxt="x.users.sub_headline">
          <DropdownBtn btnTxt={t('x.users.add_users')} menuItems={menuItems} withBorder />
        </ListPageHeader>
        <PageContentContainer>
          <ListMargin>
            {this.state.initialLoading ? (
              <SpinnerMargin>
                <LoadingSpinner />
              </SpinnerMargin>
            ) : (
              <>
                <InfoSortingBlock>
                  {pagination && (
                    <ItemsAmount
                      pagination={pagination}
                      onePageKey="x.users.list.users_amount"
                      multyPageKey="x.users.list.users_amount_from_to"
                    />
                  )}
                  <FilterSortCol>
                    <SearchWrapper>
                      <SimpleSearchInput
                        onKeyDown={this.onKeyDown}
                        placeholder={{
                          key: 'x.user.management.search',
                          value: t('x.user.management.search'),
                        }}
                        onChange={this.onChangeInput}
                        initValue={this.state.search}
                      />
                    </SearchWrapper>
                    <DeactivatedWrapper>
                      <CheckBox
                        id="show_deactivated"
                        labelKey="x.users.list.users_deactivated"
                        label={t('x.users.list.users_deactivated')}
                        isChecked={this.state.showDeactivated}
                        onChange={this.setShowDeactivated}
                      />
                    </DeactivatedWrapper>
                    <SortSelector
                      sortingOptions={userSortingOptions}
                      selectedSorting={this.state.sort}
                      sortList={this.changeSorting}
                      selectorLabelKey="x.user.list.sort.label"
                      selectorWithLabel
                    />
                  </FilterSortCol>
                </InfoSortingBlock>
                <InfoSortingBlock>
                  <FilterFilterCol>
                    {this.getFilters().map((filter: attrFilter) => {
                      return (
                        <SortSelector
                          sortingOptions={filter.options}
                          sortList={this.applyFilters(filter.id)}
                          selectorLabelKey={filter.labelKey + ':'}
                          selectorWithLabel
                          selectedSorting={filter.activeSelection}
                          key={filter.id}
                        />
                      );
                    })}
                  </FilterFilterCol>
                </InfoSortingBlock>
                <AddUserForm fetchUsers={this.fetchUsers} updateAttributes={this.updateAttributes} t={t} />
                <UserList
                  t={t}
                  changeOpenItem={this.changeOpenItem}
                  openItem={this.state.openItem}
                  updateAttributes={this.updateAttributes}
                  history={history}
                  location={location}
                  getFetchUserParams={this.stateToConfig}
                />
                {pagination &&
                  !this.state.initialLoading &&
                  !this.props.loadingUsers &&
                  pagination.total > pagination.per_page && (
                    <Pagination
                      t={t}
                      totalItems={pagination.total}
                      pageSize={pagination.per_page}
                      onChange={this.changePage}
                      marginTop={60}
                      history={history}
                      location={location}
                      defaultCurrPage={this.state.page}
                    />
                  )}
                <TextBtnPageFooter
                  t={t}
                  infoKey="x.users.list.footer.info.text"
                  btnKey="x.users.list.footer.info.button"
                  linkUrl="/company/attributes"
                />
              </>
            )}
          </ListMargin>
        </PageContentContainer>
        <ConfirmationModal
          show={this.state.isShowingConfirmationModal}
          modalBody={t('x.users.invite_users_confirmation_modal')}
          onCancel={this.onCancelInvite}
          onSubmit={() => this.inviteAll(this.state.inviteTo)}
          submitButtonText={
            this.state.inviteTo === InviteTo.spaceloop
              ? t('x.users.invite_users_to_spaceloop_button')
              : t('x.users.invite_users_button')
          }
        />
      </main>
    );
  }

  // this generates the url for both browser and rest (api) call
  private stateToConfig = (forRest?: boolean): FilterAndSortingConf => {
    return {
      page: !forRest && this.state.page === 1 ? undefined : this.state.page,
      sort: !forRest && this.state.sort === userSortingOptions[0].name ? undefined : this.state.sort,
      search: this.state.search || undefined,
      'filter.group':
        (this.state.selectedAttrFilters &&
          Object.keys(this.state.selectedAttrFilters)
            .map((k: string) => k + ':' + this.state.selectedAttrFilters[k])
            .join(',')) ||
        undefined,
      'filter.deactivated': this.state.showDeactivated || undefined,
      open: this.state.openItem,
    };
  };

  private setUrlBasedOnState() {
    this.props.history.replace({ search: queryString.stringify(this.stateToConfig()) });
  }

  private urlToState = (): State => {
    const searchStrObj: FilterAndSortingConf = queryString.parse(this.props.location.search);
    const sortFromUrl = userSortingOptions.find(sortingOption => sortingOption.name === searchStrObj.sort);
    let deactivated;
    if (searchStrObj['filter.deactivated']) {
      if (typeof searchStrObj['filter.deactivated'] === 'string') {
        deactivated = JSON.parse(searchStrObj['filter.deactivated']);
      }
    }
    return {
      ...this.state,
      sort: (sortFromUrl && sortFromUrl.name) || userSortingOptions[0].name,
      page: searchStrObj.page || 1,
      search: searchStrObj.search || undefined,
      showDeactivated: deactivated,
      selectedAttrFilters:
        (searchStrObj['filter.group'] &&
          searchStrObj['filter.group'].split(',').reduce((acc: any, curr: string) => {
            const [key, value] = curr.split(':');
            acc[key] = value;
            return acc;
          }, {})) ||
        {},
      openItem: searchStrObj.open,
      isShowingConfirmationModal: false,
    };
  };
}

const mapStateToProps = (state: StoreI): StateProps => ({
  pagination: state.userManagement.pagination,
  loadingUsers: state.userManagement.loading,
  attributes: getAttributes(state),
  company: state.user.session.company,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchUser: uuid => dispatch(fetchUser(uuid)),
  fetchUsers: params => dispatch(fetchUsers({ ...params, sort: params.sort || userSortingOptions[0].name })),
  fetchTimeZones: () => dispatch(fetchTimeZones()),
  fetchLocales: () => dispatch(fetchLocales()),
  fetchAttributesList: () => dispatch(fetchAttributesList()),
  updateAttributes: (userId, value) => dispatch(updateAttributes(userId, value)),
  changeAdmin: (userId, isAdmin) => dispatch(changeAdmin(userId, isAdmin)),
  inviteAll: (section?) => dispatch(inviteAll(section)),
  showNotification: notification => dispatch(showNotification(notification)),
});

const UserManagementWithTranslation = withTranslation()(UserManagement);
const UserManagementSuperComponent = connect(
  mapStateToProps,
  mapDispatchToProps,
)(UserManagementWithTranslation);

export default UserManagementSuperComponent;
